import { type ReactNode, useMemo } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { AMBIENT_SCRIBE_STATUS_PAUSED } from "features/medical-scribe/medical-scribe-constants";
import type { MedicalScribe } from "features/medical-scribe/medical-scribes-section/medical-scribes.type";
import PausedStatusWarningAlert from "features/medical-scribe/paused-edit/paused-status-warning-alert.component";
import type { AmbientScribe } from "services/types/ambient-scribe.type";
import { getFullName } from "utils/common";

import ScribeAudioPlayer from "./scribe-audio-player.component";
import ScribeMetadataHeader from "./scribe-metadata-header.component";

type ScribeDetailsHeaderProps = {
  remote: AmbientScribe | undefined;
  local: MedicalScribe | undefined;
  remoteMedicalScribeId: number | undefined;
  duration: string | undefined;
  /** A Complete is running; the paused warning is about to stop being true. */
  isCompleting: boolean;
  /** The Resume / Complete buttons, when this recording offers them. */
  pausedActionButtons?: ReactNode;
  audioUrl: string | undefined;
  isRecordingInProgress: boolean;
  hasModeTabs: boolean;
};

/** Who, when and how long, then the recording's player (or its Resume / Complete buttons alone). */
export default function ScribeDetailsHeader({
  remote,
  local,
  remoteMedicalScribeId,
  duration,
  isCompleting,
  pausedActionButtons,
  audioUrl,
  isRecordingInProgress,
  hasModeTabs
}: ScribeDetailsHeaderProps) {
  const patient = useMemo(() => {
    if (remote?.patient) {
      return {
        ...remote.patient,
        fullName: getFullName({
          firstName: remote.patient?.first_name || "",
          lastName: remote.patient?.last_name || "",
          title: remote.patient?.title,
          suffix: remote.patient?.suffix
        })
      };
    }
    return local?.patient;
  }, [local?.patient, remote?.patient]);

  const showPausedWarning =
    remote?.status === AMBIENT_SCRIBE_STATUS_PAUSED && !isCompleting;
  // The player stays hidden while any recording is in progress.
  const hasPlayerContent = Boolean(
    audioUrl || local?.local_id || remote?.id || pausedActionButtons
  );
  const showAudioPlayer = !isRecordingInProgress && hasPlayerContent;

  return (
    <Stack sx={hasModeTabs ? { pt: 2, flexShrink: 0 } : undefined}>
      <ScribeMetadataHeader
        createdAt={remote?.created_at ?? local?.created_at}
        patient={patient}
        status={remote?.status ?? local?.status}
        duration={duration}
        scribeId={remote?.id}
      />

      {showPausedWarning && <PausedStatusWarningAlert />}

      {pausedActionButtons && !showAudioPlayer && (
        <Box sx={{ mb: 1 }}>{pausedActionButtons}</Box>
      )}

      {showAudioPlayer && (
        <ScribeAudioPlayer
          audioUrl={audioUrl}
          remoteMedicalScribeId={remoteMedicalScribeId}
          localId={local?.local_id}
          remoteRecordingUrl={remote?.recording}
          status={remote?.status}
          disabled={isRecordingInProgress}
          trailingActions={pausedActionButtons}
        />
      )}
    </Stack>
  );
}
